/**
 * Decides whether to nudge a member about installing the app.
 *
 * Two rules, and both exist to stop it being annoying:
 *
 *   1. It only shows when alerts are genuinely not reaching their phone yet.
 *      Someone already set up never sees it, on either platform.
 *   2. Dismissal lasts a fortnight, not forever. Forever means a member who taps
 *      it away on day one never hears about phone alerts again, which defeats
 *      the point; every-launch nagging is the other failure.
 */

import { useCallback, useEffect, useState } from "react";
import { isPushSubscribed } from "../utils/pushRegistration";

const DISMISSED_KEY = "installNudgeDismissedAt";
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

function snoozed() {
  try {
    const at = Number(window.localStorage.getItem(DISMISSED_KEY) || 0);
    return at > 0 && Date.now() - at < SNOOZE_MS;
  } catch (e) {
    return false;
  }
}

export default function useInstallNudge(signedIn) {
  const [show, setShow] = useState(false);
  const [checkId, setCheckId] = useState(0);

  useEffect(() => {
    if (!signedIn || snoozed()) {
      setShow(false);
      return;
    }

    let cancelled = false;
    (async () => {
      // The thing we actually care about is whether alerts reach their phone,
      // not whether they installed. Those are the same question on an iPhone,
      // where push needs the Home Screen app, and different on Android, where
      // Chrome delivers from a tab — so nagging an Android member who already
      // gets alerts would be nagging them over nothing.
      const subscribed = await isPushSubscribed();
      if (!cancelled) setShow(!subscribed);
    })();

    return () => {
      cancelled = true;
    };
  }, [signedIn, checkId]);

  /** Re-run the check — call after the guide closes, so a member who has just
   *  turned alerts on doesn't keep seeing the banner. */
  const recheck = useCallback(() => setCheckId((n) => n + 1), []);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch (e) {
      // Private mode. The banner will come back next launch, which is survivable.
    }
    setShow(false);
  }, []);

  return { show, dismiss, recheck };
}
